// ── user-form-modal: the create/edit user Modal (POST/PATCH users) ─────────
// Wave 1 (Users). One @vjal/ui Modal that backs both the create flow (POST
// /users) and the edit flow (PATCH /users/:id). Staff-only (server re-checks and
// 403s). The ROLE select only lists roles the ACTING role may grant
// (grantableRoles) — granting the `admin` role requires `super_admin`, so an
// `admin` actor never even sees the admin/super_admin options; the server
// re-checks and a 403 is rendered honestly inline.
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE via describeUserWriteError —
// never swallowed, never faked. The page owns the mutation and closes + toasts
// ONLY after the backend confirms.

import { isEmailShaped } from "../../domain/email-shape";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Alert, AsyncSelect, Button, Checkbox, humanizeLabel, Input, Modal, Select } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { getCoachingServices } from "../../services/coaching-service-provider";
import type { CoachingCohortDto, CoachingCompanyDto, CoachingUserDto, ListResponse } from "../../services/coaching-dtos";
import { getRoleDefinition, type CoachingRole } from "../../domain/roles";
import { describeUserWriteError, grantableRoles, USER_STATUSES, type UserStatus } from "./users-write";
import { destructiveButtonClass } from "../../components/ui-classes";
import { userDisplayName } from "./user-detail-helpers";

const services = getCoachingServices();

export interface UserFormValues {
  name: string;
  email: string;
  role: CoachingRole;
  status: UserStatus;
  companyId: string;
  cohortIds: string[];
}

export interface UserFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** The acting role — gates which roles can be granted. */
  actorRole: CoachingRole;
  user?: CoachingUserDto | null;
  onClose: () => void;
  onSubmit: (values: UserFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  name?: string;
  email?: string;
  role?: string;
  companyId?: string;
  cohortIds?: string;
}

function coerceRole(value: string | undefined, fallback: CoachingRole): CoachingRole {
  const role = getRoleDefinition((value as CoachingRole) ?? fallback).role;
  return role;
}

function initialValues(
  mode: "create" | "edit",
  user: CoachingUserDto | null | undefined,
  options: CoachingRole[]
): UserFormValues {
  const fallbackRole = options[0] ?? "coach";
  if (mode === "edit" && user) {
    // The form edits org membership: seed from membershipStatus (org_user), not the profile status.
    // Only an explicitly active or suspended membership keeps that state; removed, inactive or any
    // unknown status seeds "inactive", so saving an unrelated edit never sends active:true and
    // reactivates a suspended or removed membership.
    const lifecycle = user.membershipStatus ?? user.status;
    const companyId = user.company_id ?? user.ypo?.companyIds?.[0];
    const cohortIds = user.cohort_ids ?? user.leadCohortIds?.map((id) => String(id)) ?? [];
    return {
      name: user.name ?? "",
      email: user.email ?? "",
      role: coerceRole(user.role, fallbackRole),
      status: lifecycle === "active" || lifecycle === "suspended" ? lifecycle : "inactive",
      companyId: companyId == null ? "" : String(companyId),
      cohortIds
    };
  }
  return { name: "", email: "", role: fallbackRole, status: "active", companyId: "", cohortIds: [] };
}

export function UserFormModal({
  open,
  mode,
  actorRole,
  user,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: UserFormModalProps) {
  // The roles this actor may grant. In edit mode the user's CURRENT role is kept
  // visible even if the actor can't grant it (so editing a user's name doesn't
  // silently drop their role from the picker) — but it stays the only ungrantable
  // option, and the server re-checks any change.
  const grantable = useMemo(() => grantableRoles(actorRole), [actorRole]);
  const roleOptions = useMemo(() => {
    if (mode === "edit" && user?.role) {
      const current = coerceRole(user.role, grantable[0] ?? "coach");
      return grantable.includes(current) ? grantable : [current, ...grantable];
    }
    return grantable;
  }, [mode, user, grantable]);

  const [values, setValues] = useState<UserFormValues>(() => initialValues(mode, user, roleOptions));
  const [reactivateArmed, setReactivateArmed] = useState(false);
  const lifecycle = user?.membershipStatus ?? user?.status;
  const originallyInactive = mode === "edit" && (lifecycle === "inactive" || lifecycle === "removed");
  const reactivatingUser = originallyInactive && values.status === "active";
  const close = () => {
    setReactivateArmed(false);
    onClose();
  };
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();
  const companyQuery = useHonestQuery<ListResponse<CoachingCompanyDto>>(
    coachingQueryKeys.list("companies.list"),
    () => services.companies.list(),
    { enabled: open && values.role === "company_admin" }
  );
  const cohortQuery = useHonestQuery<ListResponse<CoachingCohortDto>>(
    coachingQueryKeys.list("cohorts.list"),
    () => services.cohorts.list(),
    { enabled: open && values.role === "cohort_lead" }
  );
  const companyOptions = useMemo(
    () => (companyQuery.data?.items ?? []).map((company) => ({ value: String(company.id), label: company.name || "Unnamed company" })),
    [companyQuery.data]
  );
  const selectableCohorts = useMemo(
    () => (cohortQuery.data?.items ?? []).filter((cohort) => cohort.status !== "cancelled" && cohort.status !== "archived"),
    [cohortQuery.data]
  );
  const unavailableCohortIds = useMemo(() => {
    if (!cohortQuery.data) return [];
    const selectableIds = new Set(selectableCohorts.map((cohort) => String(cohort.id)));
    return values.cohortIds.filter((cohortId) => !selectableIds.has(String(cohortId)));
  }, [cohortQuery.data, selectableCohorts, values.cohortIds]);

  // Seed the form ONLY on the open false->true transition (and when the target
  // user identity changes), never on every parent re-render — otherwise an
  // in-progress edit would be clobbered by a background re-render. The ref tracks
  // the last (open, userId) we seeded for so a stable-prop re-render is a no-op.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const userKey = user?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== userKey)) {
      setValues(initialValues(mode, user, roleOptions));
      setReactivateArmed(false);
      setErrors({});
      seededFor.current = userKey;
    }
    wasOpen.current = open;
    // roleOptions is derived from (mode, user) which are already deps; it is not
    // an independent input, so it is intentionally omitted to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, user]);

  const set = <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) => {
    setReactivateArmed(false);
    setValues((current) => ({ ...current, [key]: value }));
  };

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Name is required.";
    const email = values.email.trim();
    if (!email) next.email = "Email is required.";
    else if (!isEmailShaped(email)) next.email = "Enter a valid email address.";
    if (values.role === "company_admin" && !values.companyId) {
      next.companyId = "Choose the company this admin manages.";
    }
    if (values.role === "cohort_lead" && values.cohortIds.length === 0) {
      next.cohortIds = "Choose at least one cohort for this lead.";
    }
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    if (reactivatingUser && !reactivateArmed) {
      setReactivateArmed(true);
      return;
    }
    try {
      await onSubmit({
        name: values.name.trim(),
        email: values.email.trim(),
        role: values.role,
        status: values.status,
        companyId: values.companyId,
        cohortIds: values.cohortIds
      });
    } catch {
      // The owning mutation surfaces the error via `submitError`.
    } finally {
      setReactivateArmed(false);
    }
  }

  const topError = describeUserWriteError(submitError);

  return (
    <Modal open={open} onClose={close} title={mode === "create" ? "Add user" : "Edit user"} className="coaching-user-modal">
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex min-w-0 flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Input
          label="Name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          error={errors.name}
          autoComplete="name"
          className="min-w-0"
          required
        />
        <Input
          label="Email"
          type="email"
          value={values.email}
          onChange={(event) => set("email", event.target.value)}
          error={errors.email}
          autoComplete="email"
          className="min-w-0"
          required
        />
        <Select
          label="Role"
          value={values.role}
          error={errors.role}
          onChange={(event) => set("role", event.target.value as CoachingRole)}
          hint="Granting the admin role requires a super admin."
          className="min-w-0"
        >
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {humanizeLabel(role)}
            </option>
          ))}
        </Select>
        {values.role === "company_admin" && (
          <AsyncSelect
            label="Company"
            value={values.companyId}
            options={companyOptions}
            placeholder="Select a company…"
            loading={companyQuery.posture === "loading"}
            loadError={
              companyQuery.posture !== null && companyQuery.posture !== "empty" && companyQuery.posture !== "loading"
                ? "Companies are unavailable right now. Retry to load them."
                : undefined
            }
            onRetry={companyQuery.retry}
            emptyLabel="No companies available — create a company first."
            error={errors.companyId}
            required
            onChange={(event) => set("companyId", event.target.value)}
            className="min-w-0"
          />
        )}

        {values.role === "cohort_lead" && (
          <fieldset className="min-w-0" aria-describedby={errors.cohortIds ? `${formId}-cohort-error` : undefined}>
            <legend className="mb-1 block text-sm font-medium text-ink-secondary">
              Cohorts<span aria-hidden="true" className="text-err ml-0.5">*</span>
            </legend>
            {cohortQuery.posture === "loading" && <p role="status" className="text-sm text-ink-muted">Loading cohorts…</p>}
            {cohortQuery.posture !== null && cohortQuery.posture !== "loading" && cohortQuery.posture !== "empty" && (
              <p role="alert" className="text-sm text-err-ink">
                Cohorts are unavailable right now. <button type="button" onClick={cohortQuery.retry} className="underline">Retry to load them.</button>
              </p>
            )}
            {cohortQuery.posture === "empty" && (
              <p className="text-sm text-ink-muted">No cohorts available — create an active cohort first.</p>
            )}
            {cohortQuery.posture === null && selectableCohorts.length === 0 && unavailableCohortIds.length === 0 && (
              <p className="text-sm text-ink-muted">No eligible cohorts available — create an active cohort first.</p>
            )}
            {selectableCohorts.map((cohort) => (
              <Checkbox
                key={cohort.id}
                label={cohort.name || "Unnamed cohort"}
                checked={values.cohortIds.includes(String(cohort.id))}
                onChange={(event) => {
                  const cohortId = String(cohort.id);
                  set("cohortIds", event.target.checked
                    ? [...values.cohortIds, cohortId]
                    : values.cohortIds.filter((id) => id !== cohortId));
                }}
              />
            ))}
            {unavailableCohortIds.map((cohortId) => (
              <Checkbox
                key={`unavailable-${cohortId}`}
                label={`Cohort ${cohortId} (no longer available)`}
                checked
                onChange={() => set("cohortIds", values.cohortIds.filter((id) => id !== cohortId))}
              />
            ))}
            {errors.cohortIds && <p id={`${formId}-cohort-error`} role="alert" className="mt-1 text-sm text-err-ink">{errors.cohortIds}</p>}
          </fieldset>
        )}

        {mode === "edit" && (
          <Select
            label="Status"
            value={values.status}
            onChange={(event) => set("status", event.target.value as UserStatus)}
            hint="Inactive users are deactivated with the separate confirmation action."
            className="min-w-0"
          >
            {(values.status === "inactive" ? ["inactive", "active"] : USER_STATUSES).map((status) => (
              <option key={status} value={status}>
                {status === "active" ? "Active" : status === "suspended" ? "Suspended" : "Inactive"}
              </option>
            ))}
          </Select>
        )}
        {reactivateArmed && (
          <Alert variant="warning">
            Reactivate {userDisplayName(user)}? They will be able to sign in again and regain their Coaching role.
          </Alert>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={reactivateArmed ? "danger" : "primary"}
            className={reactivateArmed ? destructiveButtonClass : undefined}
            isLoading={submitting}
          >
            {mode === "create" ? "Create user" : reactivateArmed ? "Reactivate user" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
